import React, { useState, useRef, useEffect } from 'react';
import { ChevronRight, Search, MoreHorizontal, Settings, X, ChevronsLeft, ChevronsRight, ChevronLeft } from 'lucide-react';
import { useGetCategoryList, useAddCategory, useUpdateCategoryStatus, useUpdateCategory, useDeleteCategory } from "../../../hooks/usePromptGroup"
import { useListLanguages } from "../../../hooks/useLanguages"
import { selectedLanguageStore, setSelectedLanguage } from '../../../stores/selectedLanguage';
import { useStore } from '@tanstack/react-store';
import { useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import Modal from '../../common/ModalComponent';


const PromptGroupsManagement = () => {
    const { selectedLanguage } = useStore(selectedLanguageStore)
    const [languages, setLanguages] = useState([]);
    const [categories, setCategories] = useState([]);
    const [title, setTitle] = useState("")
    const [searchTerm, setSearchTerm] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [totalPages, setTotalPages] = useState(10);
    const [showAddCategoryModal, setShowAddCategoryModal] = useState(false);
    const [showEditCategoryModal, setShowEditCategoryModal] = useState(false);
    const [showDeleteCategoryModal, setShowDeleteCategoryModal] = useState(false);
    const [openDropdown, setOpenDropdown] = useState(null);
    const [editingGroup, setEditingGroup] = useState(null);
    const [deletingGroup, setDeletingGroup] = useState(null);
    const { data: categoriesResponse, isSuccess: isSuccessCategories } = useGetCategoryList(`?language=${selectedLanguage}&search_key=${searchTerm}`)
    const { data: languagesListResponse, isSuccess: isSuccessLanguagesList } = useListLanguages('')
    const addCategoryMutation = useAddCategory();
    const updateStatusMutation = useUpdateCategoryStatus();
    const updateCategoryMutation = useUpdateCategory();
    const deleteCategoryMutation = useDeleteCategory();
    const queryClient = useQueryClient();
    const dropdownRef = useRef(null);
    const navigate = useNavigate()

    const handleGroupClick = (group) => {
        navigate(`/profile-meta/prompt-group/${group?._id}`)
    };

    const handleAddGroup = () => {
        setTitle('');
        setShowAddCategoryModal(true);
    };

    const handleEditGroup = (group) => {
        setTitle(group?.title);
        setEditingGroup(group)
        setShowEditCategoryModal(true);
    };

    const handleDeleteGroup = (group) => {
        setDeletingGroup(group)
        setShowDeleteCategoryModal(true);
    };

    const confirmDelete = () => {
        if (deletingGroup) {
            deleteCategoryMutation.mutate(deletingGroup?._id, {
                onSuccess: () => {
                    toast.success("Prompt Group has been deleted.")
                    queryClient.invalidateQueries({ queryKey: ['prompt_category'] });
                    setShowDeleteCategoryModal(false);
                },
                onError: (error) => {
                    const message = error.response?.data?.message
                    toast.error(message)
                }
            })
        }
    };

    const resetStates = () => {
        setTitle("")
    }

    const handleFormSubmit = (type) => {
        if (title == "") {
            toast.error("Group Name is not allowed to be empty");
            return false
        }
        const dataObj = {
            title,
            language: selectedLanguage
        }
        if (type === "edit") {
            dataObj._id = editingGroup?._id
        }
        if (type === 'add') {
            addCategoryMutation.mutate(dataObj, {
                onSuccess: () => {
                    toast.success("New Prompt Group has been added.")
                    queryClient.invalidateQueries({ queryKey: ['prompt_category'] });
                    setShowAddCategoryModal(false);
                    resetStates()
                },
                onError: (error) => {
                    const message = error.response?.data?.message
                    toast.error(message)
                }
            });
        } else if (type === 'edit') {
            updateCategoryMutation.mutate(dataObj, {
                onSuccess: () => {
                    toast.success("Prompt Group has been updated.")
                    queryClient.invalidateQueries({ queryKey: ['prompt_category'] });
                    setShowEditCategoryModal(false);
                    resetStates()
                },
                onError: (error) => {
                    const message = error.response?.data?.message
                    toast.error(message)
                }
            });
        }
    };

    const toggleStatus = (id, currentStatus) => {
        updateStatusMutation.mutate({
            status: currentStatus == 1 ? 3 : 1,
            _id: id
        }, {
            onSuccess: () => {
                toast.success("Prompt group status updated.")
                queryClient.invalidateQueries({ queryKey: ['prompt_category'] });
            },
            onError: (error) => {
                const message = error.response?.data?.message
                toast.error(message)
            }
        })
    };

    const toggleDropdown = (itemId) => {
        setOpenDropdown(openDropdown === itemId ? null : itemId);
    };

    const handleDropdownAction = (action, item) => {
        if (action === "delete") {
            handleDeleteGroup(item)
        } else if (action === "edit") {
            handleEditGroup(item)
        }
        setOpenDropdown(null);
    };

    const renderBreadcrumb = () => {
        const breadcrumbItems = [
            { label: 'Dashboard', path: 'dashboard' },
            { label: 'Profile Metadata', path: 'profile-metadata' },
            { label: 'Prompt Groups', path: 'prompt-groups' }
        ];
        return (
            <div className="flex items-center space-x-2 text-sm text-gray-500">
                {breadcrumbItems.map((item, index) => (
                    <React.Fragment key={item.path}>
                        <span className={index === breadcrumbItems.length - 1 ? 'text-gray-900' : 'text-gray-500'}>
                            {item.label}
                        </span>
                        {index < breadcrumbItems.length - 1 && <ChevronRight className="w-4 h-4" />}
                    </React.Fragment>
                ))}
            </div>
        );
    };

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setOpenDropdown(null);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    useEffect(() => {
        if (isSuccessLanguagesList) {
            setLanguages(languagesListResponse?.data?.data?.result)
        }
    }, [languagesListResponse, isSuccessLanguagesList])

    useEffect(() => {
        if (isSuccessCategories) {
            setCategories(categoriesResponse?.data?.data?.result ?? [])
            setTotalPages(categoriesResponse?.data?.data?.total_pages)
        }
    }, [isSuccessCategories, categoriesResponse])

    return (
        <div className="flex min-h-screen bg-gray-50">
            <div className="flex-1 flex flex-col">
                <div className="bg-white border-b border-gray-200 px-8 py-4">
                    {renderBreadcrumb()}
                </div>
                <div className="flex-1 p-8">
                    <div className="max-w-7xl">
                        <div className="flex items-center justify-between mb-6">
                            <div>
                                <h1 className="text-2xl font-bold text-gray-900 mb-2">Prompt Groups</h1>
                                <p className="text-gray-600">
                                    Add frequently answered prompts for users to see on your app and the website.
                                </p>
                            </div>
                            <button
                                onClick={handleAddGroup}
                                className="px-6 py-2 bg-gray-900 text-white rounded-md hover:bg-gray-800 transition-colors font-medium">
                                Add Group
                            </button>
                        </div>
                        {/* Language Selector */}
                        <div className="mb-6 flex space-x-2">
                            {languages.map((language) => (
                                <button
                                    key={language}
                                    className={`px-4 py-2 rounded ${selectedLanguage === language?.short_code ? 'bg-gray-900 text-gray-100' : 'bg-white text-gray-700'}`}
                                    onClick={() => setSelectedLanguage(language?.short_code)}>
                                    {language?.name}
                                </button>
                            ))}
                        </div>
                        {/* Search */}
                        <div className="mb-6">
                            <div className="relative max-w-md">
                                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                                <input
                                    type="text"
                                    placeholder={'Search group'}
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                />
                            </div>
                        </div>
                        {/* Table */}
                        <div className="bg-white rounded-lg border border-gray-200">
                            <table className="w-full">
                                <thead className="bg-gray-50">
                                    <tr>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">S.No</th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Group Title</th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Prompt Count</th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                                    </tr>
                                </thead>
                                <tbody className="bg-white divide-y divide-gray-200">
                                    {categories?.map((category, index) => (
                                        <tr key={category?._id} className="hover:bg-gray-50">
                                            <td className="px-6 py-4">{index + 1} </td>
                                            <td
                                                className="px-6 py-4 text-sm font-medium text-gray-900 cursor-pointer hover:text-blue-600"
                                                onClick={() => handleGroupClick(category)}>
                                                {category?.title}
                                            </td>
                                            <td className="px-8 py-4 text-sm text-gray-900">{category?.subcat_count ?? 0}</td>
                                            <td className="px-4 py-4 text-center">
                                                <button
                                                    onClick={() => toggleStatus(category?._id, category?.status)}
                                                    className="focus:outline-none">
                                                    <div className={`w-12 h-6 rounded-full flex items-center ${category?.status == 1 ? 'bg-gray-900' : 'bg-gray-300'} p-1 transition-colors duration-200`}>
                                                        <div className={`w-4 h-4 rounded-full bg-white transform transition-transform duration-200 ${category.status == 1 ? 'translate-x-6' : 'translate-x-0'}`}></div>
                                                    </div>
                                                </button>
                                            </td>
                                            <td className="px-6 py-4 text-sm text-gray-400">
                                                <div className="relative" ref={openDropdown === category?._id ? dropdownRef : null}>
                                                    <button
                                                        onClick={() => toggleDropdown(category?._id)}
                                                        className="hover:text-gray-600 p-1 rounded hover:bg-gray-100">
                                                        <MoreHorizontal className="w-5 h-5" />
                                                    </button>
                                                    {openDropdown === category?._id && (
                                                        <div className="absolute right-0 top-8 w-32 bg-white border border-gray-200 rounded-md shadow-lg z-10">
                                                            <div className="py-1">
                                                                <button
                                                                    onClick={() => handleDropdownAction('edit', category)}
                                                                    className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-100 flex items-center space-x-2">
                                                                    <Settings className="w-4 h-4" />
                                                                    <span>Edit</span>
                                                                </button>
                                                                <button
                                                                    onClick={() => handleDropdownAction('delete', category)}
                                                                    className="w-full px-4 py-2 text-left text-sm text-red-600 hover:bg-red-50 flex items-center space-x-2">
                                                                    <X className="w-4 h-4" />
                                                                    <span>Delete</span>
                                                                </button>
                                                            </div>
                                                        </div>
                                                    )}
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                            {/* Pagination */}
                            <div className="flex justify-end">
                                <div className="flex items-center">
                                    <div className="mr-4 flex justify-between">
                                        <span className="mt-1 mr-2 text-sm text-gray-500">Rows per page</span>
                                        <select
                                            className="w-[55px] border border-gray-300 rounded p-1 text-sm"
                                            value={rowsPerPage}
                                            onChange={(e) => setRowsPerPage(Number(e.target.value))}>
                                            <option value="10">10</option>
                                            <option value="25">25</option>
                                            <option value="50">50</option>
                                            <option value="100">100</option>
                                        </select>
                                    </div>
                                    <div className="flex items-center text-sm">
                                        <span className="mr-4">Page {currentPage} of {totalPages}</span>
                                        <div className="flex">
                                            <button
                                                className="p-1 border border-gray-300 rounded-l hover:bg-gray-100"
                                                onClick={() => setCurrentPage(1)}
                                                disabled={currentPage === 1}>
                                                <ChevronsLeft size={16} />
                                            </button>
                                            <button
                                                className="p-1 border-t border-b border-gray-300 hover:bg-gray-100"
                                                onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                                                disabled={currentPage === 1}>
                                                <ChevronLeft size={16} />
                                            </button>
                                            <button
                                                className="p-1 border-t border-b border-gray-300 hover:bg-gray-100"
                                                onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                                                disabled={currentPage === totalPages}>
                                                <ChevronRight size={16} />
                                            </button>
                                            <button
                                                className="p-1 border border-gray-300 rounded-r hover:bg-gray-100"
                                                onClick={() => setCurrentPage(totalPages)}
                                                disabled={currentPage === totalPages}>
                                                <ChevronsRight size={16} />
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            {/* Add Prompt Group Modal */}
            {showAddCategoryModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
                        <div className="p-6">
                            <div className="flex items-center justify-between mb-6">
                                <div>
                                    <h2 className="text-xl font-semibold text-gray-900">Add Prompt Group</h2>
                                    <p className="text-sm text-gray-500 mt-1">Create a prompt group and add prompts within</p>
                                </div>
                                <button
                                    onClick={() => setShowAddCategoryModal(false)}
                                    className="text-gray-400 hover:text-gray-600">
                                    <X className="w-6 h-6" />
                                </button>
                            </div>
                            <div className="space-y-6">
                                {/* Group Name */}
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Group name*
                                    </label>
                                    <input
                                        type="text"
                                        value={title}
                                        onChange={(e) => setTitle(e.target.value)}
                                        placeholder="Enter"
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                    />
                                </div>
                            </div>
                            <div className="flex justify-end space-x-3 mt-8 pt-6 border-t border-gray-200">
                                <button
                                    onClick={() => setShowAddCategoryModal(false)}
                                    className="px-6 py-2 text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors">
                                    Cancel
                                </button>
                                <button
                                    onClick={() => handleFormSubmit('add')}
                                    className="px-6 py-2 bg-gray-900 text-white rounded-md hover:bg-gray-800 transition-colors">
                                    Add
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
            {showEditCategoryModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
                        <div className="p-6">
                            <div className="flex items-center justify-between mb-6">
                                <div>
                                    <h2 className="text-xl font-semibold text-gray-900">Edit Prompt Group</h2>
                                    <p className="text-sm text-gray-500 mt-1">Manage a prompt group</p>
                                </div>
                                <button
                                    onClick={() => setShowEditCategoryModal(false)}
                                    className="text-gray-400 hover:text-gray-600">
                                    <X className="w-6 h-6" />
                                </button>
                            </div>
                            <div className="space-y-6">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Group name*
                                    </label>
                                    <input
                                        type="text"
                                        value={title}
                                        onChange={(e) => setTitle(e.target.value)}
                                        placeholder="Enter"
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                    />
                                </div>
                            </div>
                            <div className="flex justify-end space-x-3 mt-8 pt-6 border-t border-gray-200">
                                <button
                                    onClick={() => setShowEditCategoryModal(false)}
                                    className="px-6 py-2 text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors">
                                    Cancel
                                </button>
                                <button
                                    onClick={() => handleFormSubmit('edit')}
                                    className="px-6 py-2 bg-gray-900 text-white rounded-md hover:bg-gray-800 transition-colors">
                                    Submit
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
            <Modal
                title={<h2 className="text-lg font-semibold text-gray-900">Delete Group</h2>}
                isOpen={showDeleteCategoryModal}
                onClose={() => setShowDeleteCategoryModal(false)}
                children={
                    <div className="bg-white rounded-lg w-full">
                        <p className="text-sm text-gray-600 mb-6">
                            Are you sure you want to delete this Prompt Group? This action can't be undone
                        </p>
                        <div className="flex justify-end space-x-3">
                            <button
                                onClick={() => setShowDeleteCategoryModal(false)}
                                className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors">
                                Cancel
                            </button>
                            <button
                                onClick={confirmDelete}
                                className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors">
                                Delete Now
                            </button>
                        </div>
                    </div>
                }
            />
        </div>
    );
};
export default PromptGroupsManagement;